import Service, { service } from '@ember/service';
import { query } from '@warp-drive/utilities/json-api';
import {
  deriveSlrLoanStandingAction,
  deriveSlrLoanStandingStatus,
  normalizeSlrGoodStanding,
} from 'tio-employee/utils/slr-loan-standing-verification';
import {
  createEmployeePlanAccountDocumentRequest,
  confirmSlrLoanStandingNameMatchRequest,
} from 'tio-data/requests/slr-loan-standing-verification';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type ObservablesService from 'tio-common/services/observables';
import type { UploadFile } from 'ember-file-upload';
import type { EmployeePlanAccount } from 'tio-data/data/resources/employee-plan-account.type';
import type {
  SlrGoodStandingReason,
  SlrLoanStandingAuditState,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

// Order a problem EPA is surfaced when the employee has several and none are
// eligible — most blocking first. (`processing` is client-only and never
// derived here, but listed for completeness.)
const ATTENTION_PRIORITY: SlrLoanStandingStatus[] = [
  'ineligible',
  'cannot_determine',
  'try_again',
  'pending_upload',
  'unknown',
  'processing',
];

export default class SlrLoanStandingVerificationService extends Service {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare observables: ObservablesService;

  /**
   * Read path (tio-front-end#8714): the signed-in employee's current
   * good-standing audit state, read from the JSON:API `employee_plan_accounts`
   * resource (auth-scoped to the user) — replacing the bespoke
   * `/people/:id/slr-loan-standing-verification/current` endpoint that never
   * existed.
   *
   * Account scoping (tio-front-end#9428): alerts/messaging relate only to the
   * account receiving employer contributions — EPAs on the employee's other
   * linked accounts are excluded server-side via `filter[account_id]`. The
   * scope id is the plain `accountReceivingContributionsId` person attribute,
   * which is serialized with the session person from `/me` (no accounts
   * include required — unlike the derived `accountReceivingContributions`
   * getter, which needs materialized accounts and goes null for paid-off /
   * deactivated accounts). When no receiving account exists, there is nothing
   * to message about: returns null WITHOUT falling back to other accounts'
   * EPAs.
   *
   * Multi-EPA aggregation (within the receiving account): if ANY in-scope EPA
   * is `eligible` we surface that; otherwise the most blocking attention
   * state. Returns null when there are no in-scope EPAs.
   *
   * Deliberately NOT filtered by employee_plan_id: the session exposes the
   * contribution plan (`currentEmployee.plan.id`), not the EmployeePlan
   * join-row id that `filter[employee_plan_id]` matches — sending the plan id
   * silently excluded every real EPA and the alert never fired (the
   * reliability bug this replaced). The account filter plus the API's
   * auth-scoping to the signed-in user is the load-bearing scope; multiple
   * EPAs on the account aggregate below.
   */
  fetchCurrentState = async (options?: { reload?: boolean }) => {
    const receivingAccountId = this.sessionContext.person?.accountReceivingContributionsId;
    if (!receivingAccountId) return null;

    const { content } = (await this.store.request(
      query<EmployeePlanAccount>(
        'employee-plan-account',
        {
          'filter[account_id]': receivingAccountId,
          // The account carries the reflected observation whose borrowerName
          // the eval compared — surfaced as documentName for the Review Name
          // panel (Figma v3). The provenance chain feeds the name-match
          // card's filename callout (#10131 review: relationship, not a
          // flattened attribute).
          include: 'account,good-standing-source-document.observable-asset',
        },
        // Post-write readers pass reload to bypass the soft-cache window —
        // the PATCH is SkipCache, so it never invalidates this query's
        // cached document.
        options?.reload ? { reload: true } : {}
      )
    )) as { content: { data: EmployeePlanAccount[] } };

    const person = this.sessionContext.person;
    const employerProvidedName =
      [person?.firstName, person?.lastName].filter(Boolean).join(' ') || null;

    return aggregateAuditState(
      content.data.map((epa) => toAuditState(epa, { employerProvidedName }))
    );
  };

  /**
   * Upload path (tio-api#9814, the #7640 backend-owns-the-chain contract):
   * two JSON:API writes. The document rides the shared Observatory create
   * (`ObservablesService` — presign, S3 PUT, observable-asset,
   * observable-document), then one `employee-plan-account-documents` join row
   * names the EPA this upload verifies. The backend takes it from there:
   * reflection evaluates the referenced EPA and
   * `Slr::VerificationOutcomeBroadcaster` pushes the raw verdict on
   * `ServiceResponseChannel` keyed by the returned `assetUuid` — subscribe
   * with it, and fall back to `fetchCurrentState({ reload: true })` on
   * timeout.
   */
  createVerificationDocument = async (
    employeePlanAccountId: string,
    uploadFile: UploadFile,
    options: { batch?: string } = {}
  ) => {
    const person = this.sessionContext.person;
    if (!person) {
      throw new Error(
        '[SLR loan standing] Cannot upload a verification document without a session person'
      );
    }

    const document = await this.observables.createObservableDocument(uploadFile, {
      person,
      batch: options.batch,
    });
    if (!document.id) {
      throw new Error('[SLR loan standing] Observable document saved without an id');
    }

    await this.store.request(
      createEmployeePlanAccountDocumentRequest(employeePlanAccountId, document.id)
    );

    return {
      documentId: document.id,
      assetUuid: document.observableAsset?.uuid ?? null,
    };
  };

  /**
   * Record the participant's answer to the fuzzy name-match prompt
   * (`name_match_confirmation_required`) by PATCHing `name_match_confirmation`
   * on the EPA (tio-api#8054). The PATCH re-evaluates good standing inline
   * server-side, but its response is rendered from the pre-eval record — so
   * refetch (reload: the PATCH is SkipCache and never invalidates the query's
   * cached document) for the settled state, falling back to deriving from the
   * PATCH response if the refetch comes back empty. That response is a RAW
   * JSON:API resource (`{ id, attributes }`), not a materialized record —
   * unwrap it before deriving.
   */
  confirmNameMatch = async (employeePlanAccountId: string, confirmed: boolean) => {
    const { content } = (await this.store.request(
      confirmSlrLoanStandingNameMatchRequest(
        employeePlanAccountId,
        confirmed ? 'confirmed' : 'denied'
      )
    )) as { content: { data: { id: string | null; attributes: Record<string, unknown> } } };

    const settled = await this.fetchCurrentState({ reload: true });
    if (settled) return settled;

    const raw = content.data;
    return toAuditState({
      id: raw.id,
      ...(raw.attributes as Partial<EmployeePlanAccount>),
    } as EmployeePlanAccount);
  };
}

function toAuditState(
  epa: EmployeePlanAccount,
  context?: { employerProvidedName?: string | null }
): SlrLoanStandingAuditState {
  const goodStanding = normalizeSlrGoodStanding(epa.goodStanding);
  const reason = (epa.goodStandingReason || null) as SlrGoodStandingReason | null;
  const observation = epa.account?.observation as Record<string, unknown> | undefined;
  // Observation-hash keys are dasherized on the wire (cf. account.ext reading
  // 'interest-weighted-average'); camel fallback covers demo fixtures.
  const documentName = observation?.['borrower-name'] ?? observation?.['borrowerName'];

  return {
    employeePlanAccountId: epa.id,
    status: deriveSlrLoanStandingStatus(goodStanding, reason),
    primaryReason: epa.goodStandingReason || null,
    action: deriveSlrLoanStandingAction(goodStanding, reason),
    sourceDocumentFilename: epa.goodStandingSourceDocument?.observableAsset?.filename ?? null,
    featureEnabled: true,
    employerProvidedName: context?.employerProvidedName ?? null,
    documentName: typeof documentName === 'string' && documentName ? documentName : null,
    // Audit-window context (tio-api #9316/#9364). Empty (null / false) for
    // eligible accounts and plans without an audit schedule, so the card's
    // deadline chips + period copy stay dark unless a window applies.
    auditRequiredBy: epa.auditRequiredBy ?? null,
    auditPastDue: epa.auditPastDue ?? false,
    contributionPeriodEnd: epa.contributionPeriodEnd ?? null,
  };
}

function aggregateAuditState(
  states: SlrLoanStandingAuditState[]
): SlrLoanStandingAuditState | null {
  if (states.length === 0) return null;

  const eligible = states.find((state) => state.status === 'eligible');
  if (eligible) return eligible;

  for (const status of ATTENTION_PRIORITY) {
    const match = states.find((state) => state.status === status);
    if (match) return match;
  }

  return states[0] ?? null;
}
